import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
// Import Components
import HTMLEditors from './HTMLEditors';
import DynamicVariables from './DynamicVariables';
import TemplateTextInput from './TemplateTextInput';
import TemplateCategoryInput from './TemplateCategoryInput';
// Import Enums
import { TemplateTypeEnum } from 'enums';
// Import Constants
import { l, TemplateEditorModes, TemplateInputTypes } from 'constants/common';
import { DefaultLanguage } from 'constants/template';
// Import Hooks
import useBind from 'hooks/UseBind';
import useFieldValidity from 'hooks/UseFieldValidity';
import useInputInsertion from 'hooks/UseInputInsertion';

// temporarily hardcoded value, it shows sub content name for the same template type, it enables possibility to have different contents for the same template
const templateFieldWithConfig = 'Body';

const EmailTemplateContent = ({
    templateData,
    handleChange,
    handleUIConfigChange,
    templateLangKey,
    getAssets,
    addAsset,
    dynamicVariables,
    eventId,
}) => {
    const { t } = useTranslation();

    const subjectValidationState = useFieldValidity(TemplateInputTypes.Subject, l.Subject, templateLangKey);
    const bodyValidationState = useFieldValidity(TemplateInputTypes.Body, l.Body, templateLangKey);

    const { insertionHandler, updateInsertionHandler, insertionOptions, setInsertionOptions } = useInputInsertion();

    const handleSubjectChange = useBind(handleChange, TemplateInputTypes.Subject);
    const handleCategoryChange = useBind(handleChange, TemplateInputTypes.Category);
    const handleBodyChange = useBind(handleChange, TemplateInputTypes.Body);
    const handleBodyUIConfigChange = useBind(handleUIConfigChange, templateFieldWithConfig);
    const updateSubjectInsertionHandler = useBind(updateInsertionHandler, TemplateInputTypes.Subject);
    const updateBodyInsertionHandler = useBind(updateInsertionHandler, TemplateInputTypes.Body);

    const handleApplyAiTemplate = useCallback(
        ({ Subject, Body }) => {
            handleChange(TemplateInputTypes.Body, Body, 0);
            if (Subject) {
                handleChange(TemplateInputTypes.Subject, Subject, 0);
            }
        },
        [handleChange],
    );

    const radioGroupOptions = useMemo(
        () => TemplateEditorModes.Email.map((option) => ({ ...option, label: t(option.label) })),
        [t],
    );
    const bodyValue = templateData?.[TemplateInputTypes.Body];
    const categoryValue = templateData?.[TemplateInputTypes.Category];
    const subjectValue = templateData?.[TemplateInputTypes.Subject];
    const uiConfig = templateData?.UIConfig;

    const updateDynamicVariableBodyOptions = useCallback(
        (hintText) => {
            setInsertionOptions({
                channel: TemplateTypeEnum.Email,
                hintText,
                inputFilterKey: '',
                focusedInputKey: TemplateInputTypes.Body,
            });
        },
        [setInsertionOptions],
    );

    const updateDynamicVariableSubjectOptions = useCallback(() => {
        setInsertionOptions({
            channel: TemplateTypeEnum.Email,
            hintText: '',
            inputFilterKey: 'IsSubjectItem',
            focusedInputKey: TemplateInputTypes.Subject,
        });
    }, [setInsertionOptions]);

    return (
        <div>
            <DynamicVariables
                dynamicVariables={dynamicVariables}
                clickHandler={insertionHandler}
                insertionOptions={insertionOptions}
            />
            <div className="crm-create-template-page-c-c-h-inputs-row">
                {templateLangKey === DefaultLanguage && (
                    <TemplateCategoryInput value={categoryValue} onChange={handleCategoryChange} />
                )}
                <TemplateTextInput
                    displayNameKey={l.Subject}
                    value={subjectValue}
                    onChange={handleSubjectChange}
                    onBlur={handleSubjectChange}
                    validationState={subjectValidationState}
                    updateInsertionHandler={updateSubjectInsertionHandler}
                    onFocus={updateDynamicVariableSubjectOptions}
                />
            </div>
            <HTMLEditors
                tabOptions={radioGroupOptions}
                htmlText={bodyValue}
                handleChange={handleBodyChange}
                config={uiConfig?.[templateFieldWithConfig]}
                handleConfigChange={handleBodyUIConfigChange}
                templateLangKey={templateLangKey}
                getAssets={getAssets}
                addAsset={addAsset}
                validationState={bodyValidationState}
                dynamicVariables={dynamicVariables}
                templateType={TemplateTypeEnum.Email}
                subject={subjectValue}
                eventId={eventId}
                onApplyAiTemplate={handleApplyAiTemplate}
                bodyPropertyName={TemplateInputTypes.Body}
                updateInsertionHandler={updateBodyInsertionHandler}
                onEditorFocus={updateDynamicVariableBodyOptions}
            />
        </div>
    );
};

EmailTemplateContent.propTypes = {
    templateData: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleUIConfigChange: PropTypes.func.isRequired,
    templateLangKey: PropTypes.string.isRequired,
    getAssets: PropTypes.func,
    addAsset: PropTypes.func,
    dynamicVariables: PropTypes.arrayOf(PropTypes.object),
    eventId: PropTypes.number,
};

export default EmailTemplateContent;
